"use client";

import NavbarMenuItemLink from "./navbarMenuItemLink";
import NavbarSearchButton from "./navbarSearchInput";
import useLocalizer from "@/hooks/useLocalizer";
import { Separator } from "@/components/ui/separator";
import { LanguageSwitcher } from "@/components/shared/languageSwitcher";
import { HoverButton } from "@/components/shared/hoverButton";
import { cn } from "@/lib/utils/utils";
import Sidebar from "./sidebar";
import useSticky from "@/hooks/useSticky";
import { Link, usePathname } from "@/i18n/routing";
import Flex from "@/components/core/flex";
import { useNavigation } from "@/components/shared/navigationProvider";
import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoResponse } from "@/types/info";

interface Props {
  className?: string;
  data?: InfoResponse | null;
}

export default function Navbar({ className, data }: Props) {
  const info = data?.info;
  const socialMedia = data?.socialMedia;
  const { isRtl, t } = useLocalizer();
  const { ref, sticky } = useSticky({ options: { offset: 10 } });
  const pathname = usePathname();
  const { navigations } = useNavigation();
  const memorizedRoutes = useMemo(() => {
    const orderedNavigations = navigations?.sort(
      (a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0),
    );
    return orderedNavigations?.map((route) => ({
      title: isRtl ? route.arTitle : route.enTitle,
      href: route.href,
    }));
  }, [navigations, isRtl]);

  const memorizedSidebarRoutes = useMemo(() => {
    const orderedNavigations = navigations?.sort(
      (a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0),
    );
    return orderedNavigations.map((route) => ({
      title: isRtl ? route.arTitle : route.enTitle,
      href: route.href,
      items: route?.subPages?.map((item) => ({
        title: isRtl ? item.arTitle : item.enTitle,
        href: item.href ?? "#",
      })),
    }));
  }, [navigations, isRtl]);

  return (
    <nav
      ref={ref}
      className={cn(
        "flex flex-row justify-between h-20 border border-border transition-all duration-300 ease-in-out z-9998 bg-white relative",
        sticky ? "fixed top-0 left-0 right-0" : "md:container md:mx-auto",
        className,
      )}
    >
      <ul className="lg:flex hidden flex-row items-center pt-6 px-8 gap-4 w-full h-full">
        {memorizedRoutes?.map((route, index) => (
          <li
            data-index={index}
            data-title={route.title}
            data-href={route.href}
            key={index}
            className="flex flex-col justify-between items-center gap-2 h-full relative overflow-hidden"
          >
            <NavbarMenuItemLink
              href={route.href}
              title={route.title}
              isActive={
                route.href ===
                "/" +
                  (
                    pathname?.split("/")?.[1] || pathname?.split("/")?.[0]
                  ).trim()
              }
            />
          </li>
        ))}
      </ul>
      <Sidebar
        email={info?.email}
        phone={info?.mobileNumber}
        routes={memorizedSidebarRoutes}
        socialMedia={socialMedia}
        logo={data?.info?.lightLogo?.path}
      />
      <Flex spacing={{ all: 2 }} direction="row" center className="p-1">
        <Separator orientation="vertical" />
        <LanguageSwitcher />
        <NavbarSearchButton />
        <Link
          href={`tel:${info?.mobileNumber}`}
          className="h-full"
          aria-label={`${t("buttons.bookAppointment")}: ${info?.mobileNumber}`}
        >
          <HoverButton
            variant="secondary"
            className="min-w-48 h-full text-white lg:block hidden"
          >
            {t("buttons.bookAppointment")}
          </HoverButton>
        </Link>
      </Flex>
    </nav>
  );
}

const NavbarSkeleton = () => {
  return <Skeleton className="min-w-24 min-h-10 rounded-none" />;
};
